// React and Context
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
// PrimeReact Components
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { FilterMatchMode } from 'primereact/api';
import { MarsinetContext } from '../../../../../context/marsinetProvider';
import { useToast } from '../../../../../context/ToastProvider';
// Utility Functions and Models
import { configureAxios } from '../../../../../utils/utils';
import {
  formatUtcTimestamp,
  getUtcDayRange,
} from '../../../../../utils/timezone-utils';
import {
  IngestionProps,
} from '../../../../../models/ingestionModels';
// Components
import Graphs from '../graphs/graphs';
import { ProgressBarComponent } from '../../../../../components/progress-bar/progress-bar';
import { apiServices } from '../../../../../../../src/app/services/apiServices';
import { analytics } from '../../../../../../../src/firebase';
import { logEvent } from 'firebase/analytics';
import {
  AvailableResourceActions,
  can,
  ROLES,
  splitZipName,
  getDeviceLabel,
} from '@marsinet/commons';
import { Calendar } from 'primereact/calendar';
import {
  IngestionTableDto,
  FindAllIngestionsQueryDtoSortFieldEnum as SortFieldEnum,
  FindAllIngestionsQueryDtoSortOrderEnum as SortOrderEnum,
} from 'apps/marsinet/src/app/typescript-axios';

const SORT_FIELDS: string[] = Object.values(SortFieldEnum);
const isSortField = (value: unknown): value is SortFieldEnum =>
  typeof value === 'string' && SORT_FIELDS.includes(value);

type FilterValue = string | number | boolean | Date | null;
type FilterMeta = { value: FilterValue; matchMode: FilterMatchMode };
type IngestionFilters = Record<string, FilterMeta>;

const EMPTY_FILTERS: IngestionFilters = {
  d_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  process: { value: null, matchMode: FilterMatchMode.EQUALS },
  is_session: { value: null, matchMode: FilterMatchMode.EQUALS },
  has_errors: { value: null, matchMode: FilterMatchMode.EQUALS },
  role: { value: null, matchMode: FilterMatchMode.EQUALS },
  ingestion_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
};

const toSearchPayload = (filters: IngestionFilters, timeZone: string) =>
  Object.entries(filters).reduce<Record<string, string | number | boolean>>(
    (acc, [key, meta]) => {
      const value = meta?.value;
      if (value !== null && value !== undefined && value !== '') {
        if (key === 'ingestion_date' && value instanceof Date) {
          // Envía el día local seleccionado como un intervalo UTC exacto.
          const { start, endExclusive } = getUtcDayRange(value, timeZone);
          acc.ingestion_date_from = start.toISOString();
          acc.ingestion_date_to = endExclusive.toISOString();
          return acc;
        }
        if (!(value instanceof Date)) {
          acc[key] = value;
        }
      }
      return acc;
    },
    {}
  );

export function Ingestion(props: Readonly<IngestionProps>) {
  const { selectedProducts, setSelectedProducts, initialSerialFilter } = props;
  const { access_token, setRole, setAdmin, setError, role, selectedTimezone } =
    useContext(MarsinetContext);
  const { t, i18n } = useTranslation();
  const { showToast: show } = useToast();

  const [loading, setLoading] = useState<boolean>(true);
  const [ingestions, setIngestions] = useState<IngestionTableDto[]>([]);
  const [ingestionId, setIngestionId] = useState<string>('');
  const [showGraphs, setShowGraphs] = useState<boolean>(false);
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [modelDevices, setModelDevices] = useState<string>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [zip_data, setZip_data] = useState<string>('');
  const [deviceNames, setDeviceNames] = useState({});
  const [filters, setFilters] = useState<IngestionFilters>(EMPTY_FILTERS);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  type ErrorDetail = {
    code: string;
    message: string;
    name: string;
    serial: string;
    date: string;
  };

  const [errorDetail, setErrorDetail] = useState<ErrorDetail>({
    code: '',
    message: '',
    name: '',
    serial: '',
    date: '',
  });

  const [sortField, setSortField] = useState<SortFieldEnum | undefined>(
    undefined
  );
  const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.Desc);

  const downLoadZipPermissions = can(
    role,
    AvailableResourceActions.ingestions.download_zips
  );

  useEffect(() => {
    if (!access_token) return;
    configureAxios(access_token);
    loadIngestions(page + 1, pageSize, filters, sortField, sortOrder);
    apiServices.myAccountApi.myAccountControllerGetData().then((response) => {
      const data = response.data;
      setRole(data.organization?.role);
      setAdmin(data.can_edit);
    });
  }, [access_token, page, pageSize, filters, sortField, sortOrder]);

  useEffect(() => {
    if (!initialSerialFilter) return;
    setPage(0);
    setFilters((prev) => ({
      ...prev,
      d_name: {
        value: initialSerialFilter,
        matchMode: FilterMatchMode.CONTAINS,
      },
    }));
  }, [initialSerialFilter]);

  useEffect(() => {
    if (!access_token) return;
    configureAxios(access_token);
    apiServices.devicesApi.devicesControllerFindAll().then((response) => {
      const data = response.data;
      const devices = {};
      data.forEach((device) => {
        devices[device.d_id] = {
          serial: device.serial,
          model: device.model,
          esp_version: device.esp_version,
          ceb_version: device.ceb_version,
          epos_version: device.epos_version,
          app_version: device.app_version,
          battery_cycles: device.battery_cycles,
        };
      });
      setDeviceNames(devices);
    });
  }, [access_token]);

  const initFilters = () => {
    setPage(0);
    setFilters(EMPTY_FILTERS);
  };

  const loadIngestions = async (
    page: number,
    pageSize: number,
    filters: IngestionFilters,
    sort_field: SortFieldEnum = SortFieldEnum.IngestionDate,
    sort_order: SortOrderEnum = SortOrderEnum.Desc
  ) => {
    setLoading(true);
    try {
      const response =
        await apiServices.ingestionApi.ingestionControllerFindAll({
          page,
          limit: pageSize,
          search: {
            ...toSearchPayload(filters, selectedTimezone),
            is_test: false,
          },
          sort_field,
          sort_order,
        });
      if (response.data) {
        setIngestions(response.data.data);
        setTotalRecords(response.data.total_records);
      }
    } catch (error) {
      if (error.response?.status !== 401) {
        setError(true);
        logEvent(analytics, 'error_marsinet', {
          error_message: error.message,
          error_code: error.response?.status,
          error_page: window.location.pathname,
          error_stack: error.stack || 'No stack trace',
          timestamp: new Date().toISOString(),
          browser_info: navigator.userAgent,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const buttonGraphs = (row) => {
    return (
      <div className="d-flex justify-content-center">
        <Button
          className="p-button-rounded p-button-secondary p-button-text p-button-lg"
          icon="pi pi-chart-bar"
          onClick={() => {
            setShowGraphs(true);
            setIngestionId(row.id);
            setZip_data(row.zip_data);
            setSerialNumber(formatDevice(row.d_id, 'serial'));
            setModelDevices(formatDevice(row.d_id, 'model'));
          }}
          tooltip={t('pages.ingestions.graphs')}
          tooltipOptions={{
            position: 'bottom',
            mouseTrack: true,
            mouseTrackTop: 15,
          }}
        />
      </div>
    );
  };

  const customBooleanFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: t('pages.ingestions.is_session_true'), value: true },
          { label: t('pages.ingestions.is_session_false'), value: false },
        ]}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={t('pages.ingestions.selectState')}
        className="p-column-filter"
        showClear
      />
    );
  };

  // Filtro de procesado
  const processBooleanFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={[
        { label: t('pages.ingestions.processed'), value: true },
        { label: t('pages.ingestions.notProcessed'), value: false },
      ]}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder={t('pages.ingestions.selectState')}
      className="p-column-filter"
      showClear
    />
  );

  const hasErrorsFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={[
        { label: t('pages.ingestions.with_errors'), value: true },
        { label: t('pages.ingestions.without_errors'), value: false },
      ]}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder={t('pages.ingestions.selectState')}
      className="p-column-filter"
      showClear
    />
  );

  const roleFilterTemplate = (options) => (
    <Dropdown
      value={options.value}
      options={Object.values(ROLES).map((r) => ({
        label: t(`roles.${r}`),
        value: r,
      }))}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder={t('pages.ingestions.selectRole')}
      className="p-column-filter"
      showClear
    />
  );

  const button = (row) => {
    return (
      <div className="d-flex justify-content-center">
        <Button
          className="p-button-rounded p-button-secondary p-button-text p-button-lg"
          icon="pi pi-download"
          onClick={() => {
            downloadData(row.zip_data);
          }}
          tooltip={t('pages.ingestions.download')}
          tooltipOptions={{
            position: 'bottom',
            mouseTrack: true,
            mouseTrackTop: 15,
          }}
        />
      </div>
    );
  };

  const downloadData = async (zip_data: string) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any =
        await apiServices.ingestionApi.ingestionControllerDownloadFile(
          zip_data,
          { responseType: 'blob' }
        );
      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/zip' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = splitZipName(zip_data);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        show(
          'success',
          t('messages.success'),
          t('messages.ingestion.success_donwload')
        );
      } else {
        show('error', t('messages.error'), t('messages.ingestion.empty_file'));
      }
    } catch (_error) {
      show(
        'error',
        t('messages.error'),
        t('messages.ingestion.error_downloading')
      );
    }
  };

  const formatDevice = (d_id: string, property: string) => {
    const device = deviceNames[d_id];
    return device ? device[property] : '';
  };

  const clearFilter = () => {
    initFilters();
  };

  // Columna para mostrar el código de error traducido
  // o el mensaje literal si no hay código
  const errorCodeColumnBody = (rowData) => {
    if (!rowData.error_msg) return null;
    return (
      <div className="pl-2 flex align-items-center gap-2">
        <span>{t('pages.ingestions.see_details')}</span>
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-secondary p-button-text p-button-sm"
          style={{ width: '2rem', height: '2rem' }}
          onClick={() => {
            setErrorDetail({
              code: rowData.error_code ? t(`pages.errors.${rowData.error_code}`) : '-',
              message: rowData.error_msg || '-',
              name: rowData.zip_data ? splitZipName(rowData.zip_data) : '-',
              serial: formatDevice(rowData.d_id, 'serial') || '-',
              date: formatIngestionDate(rowData.date),
            });
            setShowErrorDialog(true);
          }}
          aria-label={t('pages.ingestions.see_details')}
          tooltip={t('pages.ingestions.see_details')}
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };
  const renderHeader = () => {
    return (
      <div className="w-full sm:w-auto flex justify-center sm:justify-start">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={clearFilter}
          className="w-full sm:w-auto text-xs sm:text-sm px-3 py-2 lg:px-4 lg:py-2"
        />
        <div className="p-inputgroup"></div>
      </div>
    );
  };

  const toISODate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;

  const formatIngestionDate = (value?: string | Date | null) => {
    if (!value) return 'N/A';
    // Las fechas de ingesta se guardan en UTC y se muestran en la zona activa.
    return formatUtcTimestamp(value, selectedTimezone, i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const dateFilterTemplate = (options) => (
    <Calendar
      value={options.value ? new Date(`${options.value}T00:00:00`) : null}
      onChange={(e) =>
        options.filterApplyCallback(e.value ? toISODate(e.value as Date) : null)
      }
      inline
    />
  );

  return (
    <div className="col-12 xl:pl-2 xl:pr-2">
      {showGraphs ? (
        <Graphs
          ingestionId={ingestionId}
          setShowGraphs={setShowGraphs}
          zip_data={zip_data}
          isTest={false}
          serialNumber={serialNumber}
          modelDevices={modelDevices}
        />
      ) : (
        <div>
          {loading ? (
            <ProgressBarComponent />
          ) : (
            <DataTable
              value={ingestions}
              paginator
              lazy
              first={page * pageSize}
              rows={pageSize}
              emptyMessage={t('emptyValuesTable')}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              selectionMode="checkbox"
              totalRecords={totalRecords}
              selection={selectedProducts}
              size="normal"
              sortField={sortField}
              sortOrder={
                sortField
                  ? sortOrder === SortOrderEnum.Asc
                    ? 1
                    : -1
                  : undefined
              }
              removableSort
              onSelectionChange={(e) => setSelectedProducts(e.value)}
              onPage={(e) => {
                setPage(e.page);
                setPageSize(e.rows);
              }}
              onSort={(e) => {
                setPage(0);
                // Third click with `removableSort` clears the sort entirely.
                if (!e.sortField || !e.sortOrder) {
                  setSortField(undefined);
                  setSortOrder(SortOrderEnum.Desc);
                  return;
                }
                if (!isSortField(e.sortField)) return;
                setSortField(e.sortField);
                setSortOrder(
                  e.sortOrder === 1 ? SortOrderEnum.Asc : SortOrderEnum.Desc
                );
              }}
              dataKey="id"
              filters={filters}
              globalFilterFields={[
                'd_name',
                'process',
                'is_session',
                'has_errors',
                'role',
                'ingestion_date',
              ]}
              header={renderHeader()}
              onFilter={(e) => {
                setPage(0);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  ...(e.filters as IngestionFilters),
                }));
              }}
            >
              <Column
                selectionMode="multiple"
                headerStyle={{ width: '3rem' }}
              ></Column>

              <Column
                field="d_name"
                header={t('pages.maintenance.ingestion.d_name')}
                filter
                showFilterMatchModes={false}
                filterPlaceholder={t('pages.ingestions.search_d_name')}
                body={(rowData) => (
                  <div className="pl-2">
                    {formatDevice(rowData.d_id, 'serial') || 'N/A'}
                  </div>
                )}
                className="col-12 sm:col-6 md:col-4 xl:col-2"
              />
              <Column
                header={t('pages.ingestions.model')}
                body={(rowData) => (
                  <div className="pl-2">
                    {getDeviceLabel(formatDevice(rowData.d_id, 'model')) ??
                      'N/A'}
                  </div>
                )}
                className="col-12 sm:col-6 md:col-4 xl:col-2"
              />

              <Column
                field="u_role"
                filterField="role"
                header={t('pages.ingestions.role')}
                filter
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={roleFilterTemplate}
                body={(rowData) => (
                  <div className="pl-2">
                    {rowData.u_role ? t(`roles.${rowData.u_role}`) : 'N/A'}
                  </div>
                )}
                className="col-12 sm:col-6 md:col-4 xl:col-2"
              />

              <Column
                field="process"
                header={t('pages.ingestions.process')}
                filter
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={processBooleanFilterTemplate}
                body={(rowData) => (
                  <div className="pl-2">
                    {rowData.process
                      ? t('pages.ingestions.processed')
                      : t('pages.ingestions.notProcessed')}
                  </div>
                )}
                className="col-12 sm:col-6 md:col-4 xl:col-3"
              />

              <Column
                field="is_session"
                header={t('pages.ingestions.is_session')}
                filter
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={customBooleanFilterTemplate}
                body={(rowData) => (
                  <div className="pl-2">
                    {rowData.is_session
                      ? t('pages.ingestions.is_session_true')
                      : t('pages.ingestions.is_session_false')}
                  </div>
                )}
                className="col-12 sm:col-6 md:col-4 xl:col-3"
              />

              <Column
                field="error_msg"
                filterField="has_errors"
                header={t('pages.ingestions.ingestion_error')}
                filter
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={hasErrorsFilterTemplate}
                body={errorCodeColumnBody}
                style={{ minWidth: '300px', maxWidth: '600px', width: '400px' }}
                className="col-12 sm:col-6 md:col-6 xl:col-5"
              />

              <Column
                field="date"
                sortField="ingestion_date"
                filterField="ingestion_date"
                header={t('pages.ingestions.date')}
                filter
                sortable
                showFilterMatchModes={false}
                showApplyButton={false}
                filterElement={dateFilterTemplate}
                body={(rowData) => (
                  <div className="pl-2">
                    {formatIngestionDate(rowData.date)}
                  </div>
                )}
                className="col-12 sm:col-6 md:col-4 xl:col-3"
              />

              <Column
                header={t('pages.ingestions.graphs')}
                body={buttonGraphs}
                className="col-12 sm:col-6 md:col-4 xl:col-2"
              />

              {downLoadZipPermissions && (
                <Column
                  header={t('pages.ingestions.download')}
                  body={button}
                  className="col-12 sm:col-6 md:col-4 xl:col-2"
                />
              )}
            </DataTable>
          )}
        </div>
      )}

      <Dialog
        header={t('pages.ingestions.detailsTitle')}
        visible={showErrorDialog}
        style={{ width: '50vw', maxWidth: 600 }}
        onHide={() => setShowErrorDialog(false)}
        modal
        closable
      >
        <div>
          <div>
            <b>{t('pages.ingestions.ingestion_error_code')}:</b>{' '}
            {errorDetail.code}
          </div>
          <div>
            <b>{t('pages.ingestions.ingestion_error')}:</b>{' '}
            {errorDetail.code
              ? t(`pages.ingestions.errors.${errorDetail.code}`, {
                defaultValue: errorDetail.message,
              })
              : errorDetail.message}
          </div>
          <div>
            <b>{t('pages.ingestions.name')}:</b> {errorDetail.name}
          </div>
        </div>
      </Dialog>
    </div>
  );
}